import { useCallback, useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { BookOpen, Sparkles, Save, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LENGTHS = [
  { v: "curta", l: "Curta (~1.500 palavras)" },
  { v: "media", l: "Média (~3.000 palavras)" },
  { v: "completa", l: "Completa (~5.000 palavras)" },
];

export default function HandoutTab({ trainingId }) {
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [opts, setOpts] = useState({ length: "media", instructions: "" });
  const poll = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get(`/trainings/${trainingId}/handout`);
    setDoc(r.data);
    if (r.data.status !== "generating") {
      setTitle(r.data.title || "");
      setContent(r.data.content || "");
      clearInterval(poll.current);
      if (r.data.status === "error") toast.error("Falha ao gerar a apostila. Tente novamente.");
    }
    return r.data;
  }, [trainingId]);

  useEffect(() => {
    load().then((d) => { if (d.status === "generating") poll.current = setInterval(load, 4000); });
    return () => clearInterval(poll.current);
  }, [load]);

  const generate = async () => {
    try {
      await api.post(`/trainings/${trainingId}/handout/generate`, opts);
      setDoc((d) => ({ ...d, status: "generating" }));
      toast.info("Gerando apostila com IA — leva cerca de 1 a 2 minutos");
      clearInterval(poll.current);
      poll.current = setInterval(load, 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao gerar"); }
  };

  const save = async () => {
    const r = await api.put(`/trainings/${trainingId}/handout`, { title, content });
    setDoc(r.data);
    toast.success("Apostila salva");
  };

  const generating = doc?.status === "generating";

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-6 shadow-none border-border h-fit space-y-4">
        <p className="overline flex items-center gap-2"><BookOpen size={12} /> Gerador de Apostila</p>
        <p className="text-xs text-muted-foreground">A IA usa título, público, escopo e metodologia do treinamento para escrever a apostila do participante. Revise e edite antes de exportar.</p>
        <div>
          <Label>Tamanho</Label>
          <select value={opts.length} onChange={(e) => setOpts({ ...opts, length: e.target.value })} className="w-full h-10 px-3 border border-input rounded-md text-sm bg-white" data-testid="handout-length">
            {LENGTHS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
          </select>
        </div>
        <div><Label>Instruções extras (opcional)</Label><Textarea rows={4} value={opts.instructions} onChange={(e) => setOpts({ ...opts, instructions: e.target.value })} placeholder="Ex.: incluir exemplos do setor de mineração" data-testid="handout-instructions" /></div>
        <Button className="w-full bg-[#D4AF37] hover:bg-[#B8961F] text-white" onClick={generate} disabled={generating} data-testid="handout-generate-btn">
          {generating ? <><Loader2 size={14} className="mr-2 animate-spin" /> Gerando…</> : <><Sparkles size={14} className="mr-2" /> {content ? "Gerar novamente" : "Gerar apostila com IA"}</>}
        </Button>
        {content && !generating && <p className="text-[11px] text-muted-foreground">Gerar novamente substitui o texto atual.</p>}
      </Card>
      <Card className="p-6 shadow-none border-border lg:col-span-2 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="overline">Conteúdo da apostila</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={save} disabled={generating || !content} data-testid="handout-save-btn"><Save size={14} className="mr-1" /> Salvar</Button>
            <Button className="bg-[#C07C88] hover:bg-[#A86571]" asChild disabled={!doc?.content} data-testid="handout-pdf-btn">
              <a href={doc?.content ? `${BACKEND_URL}/api/trainings/${trainingId}/handout/pdf` : undefined} target="_blank" rel="noreferrer" aria-disabled={!doc?.content}><FileDown size={14} className="mr-1" /> Baixar PDF</a>
            </Button>
          </div>
        </div>
        {generating ? (
          <div className="py-24 text-center" data-testid="handout-generating">
            <Loader2 size={28} className="mx-auto animate-spin text-[#D4AF37]" />
            <p className="text-sm text-muted-foreground mt-3">Escrevendo capítulos, exercícios e glossário…</p>
          </div>
        ) : (
          <>
            <div><Label>Título da capa</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="handout-title" /></div>
            <div>
              <Label>Texto (use # capítulo, ## seção, - lista, **negrito**)</Label>
              <Textarea rows={22} value={content} onChange={(e) => setContent(e.target.value)} className="font-mono text-xs leading-relaxed" placeholder="Gere com IA ou escreva aqui…" data-testid="handout-content" />
            </div>
            <p className="text-[11px] text-muted-foreground">Salve as edições antes de baixar o PDF.</p>
          </>
        )}
      </Card>
    </div>
  );
}
